import React from "react";

import { useAppContext } from "../../../../hooks/useAppContext";
import { useBottomMenu } from "../../../../hooks/useBottomMenu";
import { useLabels } from "../../../../hooks/useLabels";
import { useLogicalSelectedPage, useSelectedZone } from "../../../../hooks/useSelection";
import { IBottomMenuButton } from "../../../bottom-menu/buttons";

import { isDeleteSymbolZoneSupported } from "../menu-info";

import { useHasBottomRootMenu } from "../../../../hooks/useHasBottomRootMenu";
import { useIsProductAdvancedEditMode } from "../../../../hooks/useIsProductAdvancedEditMode";

import { BottomMenuIds, SymbolsBottomMenuIds } from "../../../bottom-menu/paths";
import { SymbolsRootMenu } from "./symbols-root-menu";
import { useDashboardContext } from "../../../../contexts/dashboard-context";
import { labels } from "../labels";
import { MenuIds, useMenus } from "../../../../hooks/useMenus";
import { useShowDesktopLayout } from "../../../../hooks/useShowdesktopLayout";
import { useKeyDown } from "../../../../hooks/useKeyboard";
import { useCopyPasteZone } from "../../../../hooks/useCopyPasteZone";
import { useArrowKeyZoneMove } from "../../../../hooks/useArrowKeyZoneMove";

export const SymbolsRootMenuContainer = () => {
	const { isVisible } = useBottomMenu();
	const {
		actions: { setMenu },
	} = useMenus();
	const hasRootMenu = useHasBottomRootMenu();
	const dashboard = useDashboardContext();
	const {
		editor: { selection },
	} = useAppContext();
	const isAdvancedEditModeEnabled = useIsProductAdvancedEditMode();
	const zone = useSelectedZone();
	const page = useLogicalSelectedPage();
	const symbolZone = eXpress.core.isSymbolZone(zone) ? zone : undefined;

	const showDesktopLayout = useShowDesktopLayout();

	const [[symbolsTitle, deleteTitle, positionTitle], isLoadingLabels] = useLabels([
		labels.symbols,
		...(isAdvancedEditModeEnabled ? [labels.delete, labels.position] : []),
	]);

	const menuItems: IBottomMenuButton<SymbolsBottomMenuIds | "back">[] = [
		// Symbol list is always visible
		{
			id: BottomMenuIds.SymbolsList,
			iconName: "symbols",
			isActive: isVisible(BottomMenuIds.SymbolsList),
			title: symbolsTitle,
			onClick: () => {
				setMenu(
					isVisible(BottomMenuIds.SymbolsList) ? MenuIds.Symbols : MenuIds.SymbolsList,
				);
			},
		},
	];

	function onBack() {
		selection.setCurrentZoneAndBroadcast(undefined);
		// Normally changing the selection will also update the root menu,
		// but when there is already no zone selected, it should also goto the root
		setMenu(MenuIds.Root);
	}

	// Keyboard shortcuts
	useCopyPasteZone({
		zone: symbolZone,
		destinationPage: page,
		isEnabled: isAdvancedEditModeEnabled,
	});
	useArrowKeyZoneMove({ zone: symbolZone, isEnabled: isAdvancedEditModeEnabled });

	useKeyDown((event) => {
		if (event.key === "Delete" && isAdvancedEditModeEnabled) {
			const deleteButton = menuItems.find(
				(item) => item.id === BottomMenuIds.SymbolsZoneDelete,
			);
			if (deleteButton && !deleteButton.isDisabled) {
				deleteButton.onClick({} as React.MouseEvent<HTMLButtonElement>);
			}
		}
	});

	// Position
	if (isAdvancedEditModeEnabled) {
		// eslint-disable-next-line react-compiler/react-compiler
		menuItems.push({
			id: BottomMenuIds.SymbolsZoneDepth, //qsdfds
			iconName: "bring-to-front",
			title: positionTitle,
			isDisabled: !symbolZone,
			isActive: isVisible(BottomMenuIds.SymbolsZoneDepth),
			onClick: () => {
				setMenu(
					isVisible(BottomMenuIds.SymbolsZoneDepth)
						? MenuIds.Symbols
						: MenuIds.SymbolZoneDepth,
				);
			},
		});
	}

	// Delete symbolzone button
	if (isAdvancedEditModeEnabled) {
		menuItems.push({
			id: BottomMenuIds.SymbolsZoneDelete,
			iconName: "bin",
			title: deleteTitle,
			isDisabled: !isDeleteSymbolZoneSupported(symbolZone, dashboard),
			onClick: () => {
				if (symbolZone) dashboard.removeZone(symbolZone);
			},
		});
	}

	if (isLoadingLabels || !menuItems.length) return null;

	return (
		<SymbolsRootMenu
			showBackButton={hasRootMenu && !showDesktopLayout}
			onBack={onBack}
			menuItems={menuItems}
		/>
	);
};
